import type { DataType } from "@repo/lanes";
import {
  type ColumnsConfig,
  chartRift,
  type FilterType,
  type RiftConfig,
  rift,
} from "@repo/shared/rift";
import type { GraphConfig } from "@repo/shared/types/graph-config";

import { datasets$ } from "../datasets";
import { getActiveDatasetRunId } from "../datasets/datasets.actions";
import { getVersionedRiftId } from "./dataset-rifts.helpers";

// ---------------------------------------------------------------------------
// Pure id + SQL computation
//
// The rift result/stat cache lives in TanStack Query (see
// `dataset-rifts.query.ts`); this module only builds the deterministic SQL and
// its versioned cache id. Both are pure/reactive helpers meant to be called
// inside `useValue(() => ...)`.
// ---------------------------------------------------------------------------

/** Reads dataset column metadata from the unified dataset store. */
const getDatasetColumns = (datasetId: string) =>
  datasets$[datasetId].columns.get();

/**
 * Reads the run id the UI should render against — the client-only `activeRunId`
 * when set (it lags the server `runId` while a new payload downloads),
 * otherwise the server `runId`. Keeps rift ids and payload loads pinned
 * to the version currently on screen.
 */
const getDatasetRunId = (datasetId: string) => getActiveDatasetRunId(datasetId);

/**
 * Builds the standard rift SQL for a dataset given the merged `riftConfig`,
 * `boardFilters`, and `columnsConfig`. The DuckDB source file is the dataset's
 * current run (`{runId}.arrow`). Reactive — calls
 * `datasets$[datasetId].columns.get()` to inline column types for
 * auto-aggregation. Use inside `useValue(() => ...)` to recompute on dep
 * changes.
 */
export const buildRiftSql = ({
  datasetId,
  runId,
  riftConfig,
  boardFilters,
  columnsConfig,
  columns,
}: {
  datasetId: string;
  runId: string;
  riftConfig: RiftConfig | null;
  boardFilters: FilterType[] | null;
  columnsConfig?: ColumnsConfig | null;
  /** Column metadata override (e.g. a draft-preview run's own columns). */
  columns?: Record<string, { type: DataType }> | null;
}): string => {
  const fileName = `${runId}.arrow`;
  const datasetColumns = columns ?? getDatasetColumns(datasetId);
  const columnDefs: Record<string, DataType> | undefined = datasetColumns
    ? Object.fromEntries(
        Object.entries(datasetColumns).map(([name, col]) => [name, col.type]),
      )
    : undefined;
  // Anchor relative date filters to the browser's local time so the rows
  // queried match the calendar/pill the user sees.
  return rift(fileName, riftConfig, boardFilters, columnDefs, columnsConfig, {
    now: new Date(),
  });
};

/**
 * Builds the chart-specific rift SQL. Layers chart-aware transforms
 * (x-axis grouping, date sorting, pie aggregation) on top of `buildRiftSql`.
 */
export const buildChartRiftSql = ({
  datasetId,
  runId,
  riftConfig,
  boardFilters,
  graphConfig,
  columnsConfig,
}: {
  datasetId: string;
  runId: string;
  riftConfig: RiftConfig | null;
  boardFilters: FilterType[] | null;
  graphConfig: GraphConfig;
  columnsConfig?: ColumnsConfig | null;
}): string => {
  const fileName = `${runId}.arrow`;
  const datasetColumns = getDatasetColumns(datasetId);
  const columnDefs: Record<string, DataType> | undefined = datasetColumns
    ? Object.fromEntries(
        Object.entries(datasetColumns).map(([name, col]) => [name, col.type]),
      )
    : undefined;
  return chartRift(
    fileName,
    riftConfig,
    boardFilters,
    columnDefs ?? null,
    graphConfig,
    columnsConfig,
    { now: new Date() },
  );
};

/**
 * Pure versioned hash of the rift SQL. Identifies a unique DuckDB query in the
 * rift cache (see `dataset-rifts.query.ts`). No side effects.
 */
export const getRiftId = (args: {
  datasetId: string;
  riftConfig: RiftConfig | null;
  boardFilters: FilterType[] | null;
  columnsConfig?: ColumnsConfig | null;
}): string => {
  const runId = getDatasetRunId(args.datasetId) ?? "";
  const sql = buildRiftSql({ ...args, runId });
  return getVersionedRiftId(sql, runId);
};

/** Pure versioned hash of the chart rift SQL. See `getRiftId`. */
export const getChartRiftId = (args: {
  datasetId: string;
  riftConfig: RiftConfig | null;
  boardFilters: FilterType[] | null;
  graphConfig: GraphConfig;
  columnsConfig?: ColumnsConfig | null;
}): string => {
  const runId = getDatasetRunId(args.datasetId) ?? "";
  const sql = buildChartRiftSql({ ...args, runId });
  return getVersionedRiftId(sql, runId);
};
